import type { ServiceError } from "../../service/Shared";

export type ProjectEntryMethod = "manual" | "upload";

export type ProjectRow = {
  key: string;
  displayName: string;
  projectTypeUuid: string | null;
  standardUuid: string | null;
  vvbUuid: string | null;
  addressRegion: string | null;
  addressPostcode: string | null;
  latitude: string | null;
  longitude: string | null;
  gridReference: string | null;
  addressCountryCode: string | null;
  areaNetHa: number | null;
  // Set only on a row parsed from an uploaded file - a typed row has neither a position in a file nor
  // a landholding grouping column beside it.
  rowNumber?: number;
  projectTypeName?: string;
  standardName?: string;
  verifierName?: string;
  landholdingName?: string | null;
  // The KIND of reference (Single Business Identifier, Title Number, ...) - the same list the
  // landholding form offers - and the reference itself. Together they are what makes two rows the
  // same landholding: the name is only carried along.
  landholdingReference?: string | null;
  landholdingReferenceId?: string | null;
};

export type LandholdingDetails = {
  name: string;
  reference: string | null;
  referenceId: string;
  region: string;
  latitude: string | null;
  longitude: string | null;
  gridReference: string | null;
  countryCode: string | null;
};

export type ParsedRowError = {
  // The spreadsheet row, so the user can find it in the file they have to fix and re-upload.
  rowNumber: number;
  // What the error is shown under: the project's name, or "Landholding <name>" for the landholding's
  // own failure. Without it the row number is all there is to go on.
  label?: string;
  reason: string;
};

export enum NewProjectJourneyStep {
  DETAILS = "Details",
  ACCESS = "Organisations & access",
}

export type ProjectEntryState = {
  rows: ProjectRow[];
  landholding: LandholdingDetails;
};

export type ProjectDetailsStepState = {
  entryMethod: ProjectEntryMethod;
  isLandholding: boolean;
  data: ProjectEntryState;
  // The api's last word on these rows, kept raw: the details step derives the summary box, the
  // per-field errors and the parsed table's row errors from this one list, and the access step
  // writes a failed create back into it so Previous shows each failure where it belongs. Journey
  // state rather than step state so it survives moving between the steps.
  validationErrors: ServiceError[];
};

export type CreatedProjects = {
  projectUuidByKey: Record<string, string>;
  // The one group the journey wrote, when it wrote exactly one - the manual landholding, or an
  // upload whose rows all sat under a single landholding. A host needing every group reads
  // groupUuidByKey; this stays so the post-save redirect keeps its one rule.
  groupUuid?: string;
  // Every group written so far, by the create request it came from (buildCreateRequests). An
  // upload can carry several landholdings, each its own createGroup call and its own transaction,
  // so one can be written while another is refused - a retry skips the ones recorded here.
  groupUuidByKey: Record<string, string>;
};

export type ProjectAccessStepState = {
  organisations: AddedOrganisationAssignment[];
  // projectKey -> organisation uuid. A project with no entry is owned by the creating organisation
  // (REQ-003), so the creating org's uuid never has to be stored in the draft.
  responsibleOwners: Record<string, string>;
};

export type NewProjectJourneyState = {
  currentStep: NewProjectJourneyStep;
  // The furthest step reached. Forward-only, so stepping back does not rewind the stepper.
  inProgressStep: NewProjectJourneyStep;
  details: ProjectDetailsStepState;
  access: ProjectAccessStepState;
  // Set once a save's create call succeeds and cleared by any edit to the rows. Lives on the journey
  // rather than a step because Previous unmounts the access step - without this, returning and
  // saving again would re-post the same row uuids and collide on the create the api already
  // accepted (BR-02888).
  createdProjects: CreatedProjects | null;
  // Set when Save is first clicked and never cleared (bar clearJourney): from then on the details step
  // is closed off - Previous and the stepper alike - whether the save went through, failed, or half did.
  isSaveAttempted: boolean;
};

// A row the user has added. The organisation name is captured at add time because the picker's
// options are replaced by the next search and could no longer resolve it.
export type AddedOrganisationAssignment = {
  key: string;
  organisationUuid: string;
  organisationName: string;
  organisationType: string;
  // The type-specific record behind the organisation (searchOrganisations' subOrganisationUuid),
  // captured for the organisations the user adds. createProject's developerUuid names that record
  // rather than the parent organisation, so it is what a project is created with when the access
  // list hands the Developer role to one of them. Absent on the creating organisation's own entry,
  // which is never named to the api - the caller is who it already takes the creator from.
  subOrganisationUuid?: string | null;
  // Sent verbatim as the invite's targetRole.
  role: string;
  accessLabel: string;
  projectKeys: string[];
  // What becomes of the developer this assignment displaces, keyed by projectKey - a Developer/Full
  // access add can target several projects in one go, each with its own existing developer or none
  // (BR-02726/BR-02873, mirrors InviteSelection.existingDeveloperAction).
  existingDeveloperActions?: Record<string, string | null>;
};

// The add-card's working value, before it becomes an AddedOrganisationAssignment.
export type AddOrganisationSelection = {
  organisationType: string | null;
  organisationUuid: string | null;
  access: string | null;
  projectKeys: string[];
  existingDeveloperActions?: Record<string, string | null>;
};

export type ProjectRollupOrganisation = {
  organisationUuid: string;
  organisationName: string;
  organisationType: string;
  access: string;
  // A project must keep a full-access Developer (BR-02726), and the add card's swap is the only way
  // to change which organisation that is, so that one row is never offered a remove control.
  canRemove: boolean;
};

export type ProjectRollupRow = {
  projectKey: string;
  projectName: string;
  // The landholding the project is created under, whose organisations get full access to it along
  // with the project. Null for a standalone project.
  landholdingName: string | null;
  organisations: ProjectRollupOrganisation[];
  responsibleOwnerOrganisationUuid: string;
};

export type AccessTableRow = {
  organisationUuid: string;
  organisationName: string;
  organisationType: string;
  role: string;
  isResponsibleOwner: boolean;
  canBeResponsibleOwner: boolean;
};

export type InviteTableRow = {
  invitationUuid: string;
  /** "Invitation" or "Request" - whether an organisation was invited, or asked to join. */
  inviteType?: string;
  organisationType: string;
  organisationName: string;
  access: string;
  dateSent: string;
  createdBy: string;
  status: string;
};

export type InviteSelection = {
  organisationType: string | null;
  organisationUuid: string | null;
  access: string | null;
  makeResponsibleOwner?: boolean;
  // What becomes of the developer being displaced, on a Developer invite that swaps one (BR-02873).
  existingDeveloperAction?: string | null;
};

export type SkeletonOrgDetails = {
  email: string;
  organisationDisplayName: string;
  invitedUserFirstName: string;
  invitedUserLastName: string;
};

export type EditSelection = {
  access: string | null;
  makeResponsibleOwner?: boolean;
};
